import { Dispatch, SetStateAction, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import useApiCall from '../../../../../../hooks/useApiCall';
import useGetGeneralAppData from '../../../../../../hooks/useGetGeneralAppData';
import ClientUsersService from '../../../../../../services/ClientUsersService';
import { ActionsPermissionsType, OptionType, PermissionsListFromApi, SelectActionType, SelectedPermissionType } from '../../types';

interface IUsePermissions {
  setIsLoadLoading: Dispatch<SetStateAction<boolean>>
}

export default function usePermissions({
  setIsLoadLoading,
}: IUsePermissions) {
  const [permissionsOptions, setPermissionsOptions] = useState<OptionType[]>([]);
  const [selectedPermissions, setSelectedPermissions] = useState<SelectedPermissionType[]>([]);

  const [defaultPermissions, setDefaultPermissions] = useState<SelectedPermissionType[]>([]);
  const [adminPermissions, setAdminPermissions] = useState<SelectedPermissionType[]>([]);

  const [pageBeingSettedPermissions, setPageBeingSettedPermissions] = useState({} as OptionType);
  const [permissionsOfPageModalShow, setPermissionsOfPageModalShow] = useState(false);
  const [selectedActions, setSelectedActions] = useState({} as ActionsPermissionsType);

  const { apiCall } = useApiCall();
  const { userCode, userProfile, companyCode, permissions: userPermissions } = useGetGeneralAppData();
  const navigate = useNavigate();

  function handleActionSelect(action: SelectActionType) {
    setSelectedActions((prevState) => ({
      ...prevState,
      [action.action]: action.isSelected,
    }));
  }

  function handleOpenPageBeingSettedPermissionsModal(page: OptionType) {
    setPageBeingSettedPermissions(page);
    const infoFromPageBeingShow = selectedPermissions.find((permission) => permission.pageCode === page.pageCode);
    const currentSelectedPermissionsOfPage = {
      add: infoFromPageBeingShow?.add ?? false,
      view: infoFromPageBeingShow?.view ?? false,
      update: infoFromPageBeingShow?.update ?? false,
      download: infoFromPageBeingShow?.download ?? false,
      delete: infoFromPageBeingShow?.delete ?? false,
    };
    setSelectedActions(currentSelectedPermissionsOfPage);
    setPermissionsOfPageModalShow(true);
  }

  const handlePermissionsChange = useCallback(() => {
    const pagePermissionsBeingChanged = pageBeingSettedPermissions.pageCode;

    const clonedSelectedPermissions: SelectedPermissionType[] = JSON.parse(JSON.stringify(selectedPermissions));
    const indexOfSelectedPage = clonedSelectedPermissions.findIndex((permission) => permission.pageCode === pagePermissionsBeingChanged);

    if (indexOfSelectedPage === -1) {
      clonedSelectedPermissions.push({
        pageCode: pagePermissionsBeingChanged,
        pageLabel: pageBeingSettedPermissions.pageLabel,
        add: selectedActions.add,
        view: selectedActions.view,
        update: selectedActions.update,
        delete: selectedActions.delete,
        download: selectedActions.download,
      });
      clonedSelectedPermissions.sort((a, b) => a.pageCode - b.pageCode);
    } else {
      clonedSelectedPermissions[indexOfSelectedPage] = {
        ...clonedSelectedPermissions[indexOfSelectedPage],
        add: selectedActions.add,
        view: selectedActions.view,
        update: selectedActions.update,
        delete: selectedActions.delete,
        download: selectedActions.download,
      };
    }
    setSelectedPermissions(clonedSelectedPermissions);
    setPermissionsOfPageModalShow(false);
  }, [pageBeingSettedPermissions.pageCode, pageBeingSettedPermissions.pageLabel, selectedActions.add, selectedActions.delete, selectedActions.download, selectedActions.update, selectedActions.view, selectedPermissions]);

  const getPermissionsOptions = useCallback(async () => {
    await apiCall({
      apiToCall: ClientUsersService.getPermissionsOptions,
      queryParams: { codUsu: userCode, perfilUsu: userProfile, codEmpresa: companyCode },
      onStartLoad: () => setIsLoadLoading(true),
      onEndLoad: () => setIsLoadLoading(false),
      actionAfterResponse: (response) => {
        if (!response.codigo) {
          toast.error(`Não foi possível carregar a lista de permissões. Por favor, tente abrir o formulário novamente. (${response.msg})`);
          navigate('/internalUsers');
          return;
        }
        const pagesOfPermissions: PermissionsListFromApi = response.listaPags;
        const optionsForClientUsers = pagesOfPermissions.filter((permissions) => permissions.pagTipo === 2 || permissions.pagTipo === 3);
        const filteredByUserPermissions = optionsForClientUsers.filter((permission) => {
          const userPermissionsOfPage = userPermissions.find((x) => x.codPag === permission.codPag);
          return userPermissionsOfPage?.vizualizar;
        });
        const mappedPermissions = filteredByUserPermissions.map((permissions) => ({
          pageCode: permissions.codPag,
          pageLabel: permissions.pagina,
          add: false,
          view: false,
          update: false,
          download: false,
          delete: false,
        }));
        const defaultProfilePermissions = mappedPermissions.map((permission) => ({
          pageCode: permission.pageCode,
          pageLabel: permission.pageLabel,
          add: false,
          view: false,
          update: false,
          download: false,
          delete: false,
        }));
        const adminProfilePermissions = mappedPermissions.map((permission) => ({
          pageCode: permission.pageCode,
          pageLabel: permission.pageLabel,
          add: true,
          view: true,
          update: true,
          download: true,
          delete: true,
        }));
        setPermissionsOptions(mappedPermissions);
        setAdminPermissions(adminProfilePermissions);
        setDefaultPermissions(defaultProfilePermissions);
        setSelectedPermissions(defaultProfilePermissions);
      },
      catchMessage: 'Não foi possível recuperar a lista de permissões',
      catchAction: () => navigate('/clientUsers'),
    });
  }, [apiCall, companyCode, navigate, setIsLoadLoading, userCode, userPermissions, userProfile]);

  return {
    permissionsOptions,
    selectedPermissions,
    pageBeingSettedPermissions,
    permissionsOfPageModalShow,
    selectedActions,
    handleActionSelect,
    handleOpenPageBeingSettedPermissionsModal,
    handlePermissionsChange,
    setPermissionsOfPageModalShow,
    getPermissionsOptions,
    defaultPermissions,
    adminPermissions,
    setSelectedPermissions,
  };
}
